import { Navigate, useLocation } from "react-router-dom";
import { useOrg } from "@/hooks/useOrg";
import { Loader2 } from "lucide-react";

interface RequireOnboardingProps {
  children: React.ReactNode;
}

/**
 * Route guard that ensures users have completed onboarding before accessing the app.
 *
 * Redirects to:
 * - /onboarding if user has no org
 * - /onboarding/kyc/:familyId if family status is 'kyc_pending'
 *
 * Allows through if:
 * - onboarding_status is 'active' (fully onboarded)
 * - onboarding_status is 'draft' (org just created, admin can use immediately)
 * - onboarding_status is 'data_pending' (KYC done, waiting for portfolio data)
 * - onboarding_status is NULL (legacy/demo data)
 */
export function RequireOnboarding({ children }: RequireOnboardingProps) {
  const location = useLocation();
  const { currentOrg, families, isLoading, isSFO, defaultFamily, isOrgAdmin } = useOrg();

  // Show loading state while checking org status
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // No org at all - redirect to create one
  if (!currentOrg) {
    return <Navigate to="/onboarding" state={{ from: location }} replace />;
  }

  // Org admins can always access the dashboard (they're setting things up)
  if (isOrgAdmin) {
    return <>{children}</>;
  }

  // For SFO: Check the default family's onboarding status
  // For MFO: Check if at least one family is active
  const familyToCheck = isSFO ? defaultFamily : families[0];

  if (familyToCheck) {
    const status = familyToCheck.onboarding_status;

    // Only block if status is explicitly 'kyc_pending'
    // This means KYC was started but not completed
    if (status === 'kyc_pending') {
      return <Navigate to={`/onboarding/kyc/${familyToCheck.id}`} state={{ from: location }} replace />;
    }

    // For all other statuses (active, draft, data_pending, null), allow through
    // - 'active': Fully onboarded
    // - 'draft': Just created, can start using
    // - 'data_pending': KYC done, can start using while importing data
    // - null/undefined: Legacy data
  }

  // All checks passed - render the protected content
  return <>{children}</>;
}
